import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isFulfilled } from '@reduxjs/toolkit';

import Disclaimer from 'App/common/Disclaimer/Disclaimer';
import NotificationComponent from 'App/common/Notification/Notification';
import { errorContent } from 'App/common/Notification/Notification.config';
import AddArticleModal from 'App/DetailsPage/common/AddArticleModal/AddArticleModal';
import LoadingState from 'App/DetailsPage/common/LoadingState/LoadingState';
import { extLnkMyPostRedirectV2, trackUiAppId } from 'config/track-config';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { trackInteraction, updateUserAnalytics } from 'store/analytics/analytics.thunks';
import { selectIsAuthed } from 'store/customer';
import { putDeliveryPreferences } from 'store/deliveryPreference';
import { clearNotification, createNotification } from 'store/notification';
import { clearShipmentDetails, getShipment } from 'store/shipment/details/shipmentDetails.slice';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
  hasMxMessage,
  messageExchangeConfig,
  postMxNotificationMsg,
  readMxMessage,
  readMxNotificationMsg,
} from 'utils/message-exchange.utils';
import { getUniqueDuplicateIds } from 'utils/shipment.utils';

import { getShipmentList, queryShipments } from '../../store/shipment/thunks';
import { AppLocation } from '../../utils/window.utils';
import { AccordionCard } from './common/AccordionCard/AccordionCard';
import DetailsPanel from './DetailsPanel/DetailsPanel';
import DialogueCardPanel from './DialogueCardPanel/DialogueCardPanel';
import { useCurrentArticle, useFirstArticle, useShipment } from './hooks/shipment.hooks';
import SidePanel from './SidePanel/SidePanel';
import SummaryPanel from './SummaryPanel/SummaryPanel';
import TrackingPanel from './TrackingPanel/TrackingPanel';
import { ACCORDION_DETAILS_PANEL, DETAILS_PAGE_TEST_ID } from './DetailsPage.test-ids';

import styles from './DetailsPage.module.scss';

const DetailsPage = () => {
  const [loading, setLoading] = useState(true);
  const shipmentData = useShipment();
  const currentArticle = useCurrentArticle();
  const firstArticle = useFirstArticle();
  const delegationExpiredLinkError = readMxMessage(messageExchangeConfig.delegationExpiredLink);
  const hasDeliveryChoiceMessage = hasMxMessage(messageExchangeConfig.deliveryChoices);
  const { trackingId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const customerData = useAppSelector((state) => state.customer.data);
  const isAuthed = useAppSelector(selectIsAuthed);

  const notices = shipmentData?.dynamicCommsList || [];
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  if (!trackingId) return null;

  // check for invalid trackingId
  React.useEffect(() => {
    if (/[^\w\d]/.test(trackingId)) {
      const errMsg = `The tracking number you've entered doesn't look right. Please use only letters (A-Z) and numbers (0-9).`;
      const messagePayload = { type: NotificationType.ALERT, status: NotificationStatus.ERROR, message: errMsg };
      postMxNotificationMsg(messagePayload, trackUiAppId);
      navigate('/search');
    }
  }, [trackingId]);

  // check for disruption fraud msg
  React.useEffect(() => {
    if (notices.length > 0) {
      notices.forEach((notice) => {
        if (notice.type !== 'FRAUD') return;
        const { content, linkUrl, linkText } = notice;
        const message = `${content} <a href="${linkUrl}" target="_blank">${linkText}</a>`;
        const notification: Notification = {
          type: NotificationType.ALERT,
          status: NotificationStatus.INFO,
          message,
        };
        dispatch(createNotification(notification));
      });
    }
  }, [notices]);

  // check for notification and add article mx msg on page load
  React.useEffect(() => {
    dispatch(clearNotification());

    // check for delegation expired link mx message
    const delegationExpiredArticleId = delegationExpiredLinkError?.[0]?.payload?.articleId;
    if (delegationExpiredArticleId) {
      const notification: Notification = {
        type: NotificationType.ALERT,
        status: NotificationStatus.ERROR,
        message: errorContent.articleDelegation.expiredLink(delegationExpiredArticleId),
      };
      dispatch(createNotification(notification));
    }

    // check for add article mx message
    const hasAddArticleMessage = hasMxMessage(messageExchangeConfig.articleAdd);
    if (hasAddArticleMessage) setIsModalOpen(true);

    // check for notification pill mx messages
    const notifications = readMxNotificationMsg();
    notifications.forEach((notification) => dispatch(createNotification(notification)));

    return () => {
      // clear notifications when the component is unmounted
      dispatch(clearNotification());
    };
  }, []); // Runs once

  // check for set atl (safedrop) mx message and
  // set global safedrop preference after user is authenticated
  React.useEffect(() => {
    if (!isAuthed) return;
    const atlSetPreferenceMessage = readMxMessage(messageExchangeConfig.atlSetPreference);
    const location = atlSetPreferenceMessage?.[0]?.payload?.location;
    location && dispatch(putDeliveryPreferences(location));
  }, [isAuthed]);

  // get shipment details data
  React.useEffect(() => {
    if (!customerData) return;
    dispatch(updateUserAnalytics());

    fetchShipmentDetails();
    // TODO: check whether isAuth check is redundant
    if (!customerData.isAuthenticated) return;

    return () => {
      // clear shipment detail after component unmount
      dispatch(clearShipmentDetails());
    };
  }, [customerData]);

  const fetchShipmentDetails = async () => {
    const hasSingleQueryTrackingId = hasMxMessage(messageExchangeConfig.queryTrackingId);
    let getShipmentDetailsAction;
    let watchlistAction = null;

    if (hasSingleQueryTrackingId && customerData?.isAuthenticated) {
      [getShipmentDetailsAction, watchlistAction] = await Promise.all([
        dispatch(getShipment(trackingId)),
        dispatch(getShipmentList({ ignoreErrors: true })),
      ]);
    } else {
      getShipmentDetailsAction = await dispatch(getShipment(trackingId));
    }

    if (!isFulfilled(getShipmentDetailsAction)) {
      if (getShipmentDetailsAction.payload?.status === 401) return;
      navigate('/search');
      return;
    }

    if (!customerData?.isAuthenticated || !hasSingleQueryTrackingId) {
      setLoading(false);
      return;
    }

    if (watchlistAction) {
      if (!isFulfilled(watchlistAction)) {
        setLoading(false);
        return;
      }
      const watchlist = watchlistAction.payload || [];
      const { duplicates, uniqueIds } = getUniqueDuplicateIds(
        [trackingId.toUpperCase()],
        Array.isArray(watchlist) ? watchlist : []
      );
      if (duplicates.length && !uniqueIds.length) {
        const duplicateNotification = {
          type: NotificationType.ALERT,
          status: NotificationStatus.SUCCESS,
          message: errorContent.alreadyAdded(duplicates),
        };
        dispatch(createNotification(duplicateNotification));
        setLoading(false);
        return;
      }

      const queryShipmentActionResult = await dispatch(
        queryShipments({ trackingIds: [trackingId], ignoreErrors: true })
      );

      if (!isFulfilled(queryShipmentActionResult)) {
        setLoading(false);
        return;
      } else {
        setLoading(false);
        setIsModalOpen(true);
        return;
      }
    }
  };

  // listen for redirect flow message exchange after user being authenticated
  React.useEffect(() => {
    if (!customerData?.isAuthenticated) return;
    if (!shipmentData || !Object.keys(shipmentData).length) return;
    const firstArticleArticleId = firstArticle?.articleId || '';
    if (hasDeliveryChoiceMessage && currentArticle) {
      const { userOwnership, articleRedirect, nickname, articleId } = currentArticle;
      const notification: Notification = {
        type: NotificationType.ALERT,
        status: NotificationStatus.ERROR,
        message: '',
      };
      if (!userOwnership) {
        const identifier = nickname ?? articleId;
        notification.message = errorContent.articleRedirect.noOwnership(identifier);
        dispatch(createNotification(notification));
        return;
      }
      if (!articleRedirect) {
        notification.message = errorContent.articleRedirect.noRedirect;
        dispatch(createNotification(notification));
        return;
      }
      AppLocation.assign(`${extLnkMyPostRedirectV2}/${firstArticleArticleId}`);
    }
  }, [customerData, shipmentData]);

  if (loading) return <LoadingState />;

  const DetailsPanelAccordion = () => {
    const accordionArgs = {
      id: 'detailsPanelAccordion',
      heading: <span>Details</span>,
      children: <DetailsPanel />,
      icon: null,
    };
    return (
      <AccordionCard
        {...accordionArgs}
        data-testid={ACCORDION_DETAILS_PANEL}
        className={styles.detailsAccordion}
        onToggle={(isOpen) => {
          if (!isOpen) dispatch(trackInteraction('view details'));
        }}
      />
    );
  };

  return (
    <div className={styles.detailsContainer}>
      <NotificationComponent className={styles.detailsNotification} />
      <div className={styles.details} data-testid={DETAILS_PAGE_TEST_ID}>
        <div className={styles.detailsLeftPanel}>
          <SummaryPanel />
          <TrackingPanel />
          <DialogueCardPanel />
          <DetailsPanelAccordion />
        </div>
        <div className={styles.detailsRightPanel}>
          <SidePanel />
        </div>
        <AddArticleModal isOpen={isModalOpen} handleClose={() => setIsModalOpen(false)} />
      </div>
      <Disclaimer />
    </div>
  );
};

export default DetailsPage;
